/**
 * GitHub Activity ダッシュボードのスコープ + 期間選択バー。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business N=1 のときラジオを隠しピル表示、N>=2 で `全体 / チーム / 個人` ラジオ + 期間 Select を出す。メンバー選択は app 内 MemberPicker を使い、検索 + 部署 (teamName) 絞り込みに対応する
 * @graph-connects none
 */

import { useTranslation } from 'react-i18next';
import { MemberPicker } from '../components/ui/MemberPicker/index.js';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { resolveDisplayNameByEmail } from '../lib/displayName.js';
import type { GithubActivityMember, PeriodDays, Scope } from './github-activity-search.js';

/** @graph-connects none */
export interface ScopeBarProps {
  isSingleMemberMode: boolean;
  scope: Scope;
  selectedTeam: string | undefined;
  selectedEmail: string | undefined;
  days: PeriodDays;
  members: GithubActivityMember[];
  /** member.email から users.nickName を引く Map (`docs/guidelines/frontend.md` ニックネーム優先) */
  nicknameByEmail: Map<string, string>;
  availableTeams: string[];
  dashboardScopeLabel: string;
  periodOptions: { value: string; label: string }[];
  onScopeChange: (scope: Scope) => void;
  onTeamChange: (team: string | undefined) => void;
  onEmailChange: (email: string | undefined) => void;
  onDaysChange: (days: PeriodDays) => void;
}

/** @graph-connects none */
export function GitHubActivityScopeBar(props: ScopeBarProps) {
  const { t } = useTranslation();
  const {
    isSingleMemberMode,
    scope,
    selectedTeam,
    selectedEmail,
    days,
    members,
    nicknameByEmail,
    availableTeams,
    dashboardScopeLabel,
    periodOptions,
    onScopeChange,
    onTeamChange,
    onEmailChange,
    onDaysChange,
  } = props;

  const scopeOptions: { value: Scope; label: string; description: string }[] = [
    {
      value: 'all',
      label: t('githubActivity.scope.all'),
      description: t('githubActivity.scope.allDescription'),
    },
    {
      value: 'team',
      label: t('githubActivity.scope.team'),
      description: t('githubActivity.scope.teamDescription'),
    },
    {
      value: 'member',
      label: t('githubActivity.scope.member'),
      description: t('githubActivity.scope.memberDescription'),
    },
  ];

  return (
    <Card className="animate-content-reveal">
      <CardContent>
        <div className="flex flex-wrap items-center gap-2">
          {/* N=1 のときはラジオを隠してピル表示。メンバーが増えれば自動でラジオが戻る */}
          {isSingleMemberMode ? (
            <span
              className="rounded-full border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-1 text-xs text-[var(--color-text-secondary)]"
              title={t('githubActivity.scopePill.hint')}
            >
              {t('githubActivity.scopePill.single', {
                name: members[0]
                  ? resolveDisplayNameByEmail(nicknameByEmail, members[0])
                  : dashboardScopeLabel,
              })}
            </span>
          ) : (
            <div
              role="radiogroup"
              aria-label={t('githubActivity.scopeLabelPrefix')}
              className="flex flex-wrap items-center gap-2"
            >
              {scopeOptions.map((option) => {
                const isSelected = scope === option.value;
                const isMember = option.value === 'member';
                const isTeam = option.value === 'team';
                return (
                  <div key={option.value} className="flex items-center gap-2">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      title={option.description}
                      onClick={() => {
                        onScopeChange(option.value);
                      }}
                      className={`px-3 py-1.5 rounded border text-sm transition-colors ${
                        isSelected
                          ? 'border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)] text-white'
                          : 'border-[var(--color-border)] bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-hover)]'
                      }`}
                    >
                      {option.label}
                    </button>
                    {isTeam && scope === 'team' ? (
                      <Select
                        aria-label={t('githubActivity.teamSelectLabel')}
                        options={availableTeams.map((name) => ({ value: name, label: name }))}
                        value={selectedTeam ?? ''}
                        onChange={(e) => {
                          onTeamChange(e.target.value === '' ? undefined : e.target.value);
                        }}
                        placeholder={t('githubActivity.teamSelectLabel')}
                        size="sm"
                      />
                    ) : null}
                    {isMember && scope === 'member' ? (
                      <div className="min-w-64">
                        <MemberPicker
                          aria-label={t('githubActivity.memberSelectLabel')}
                          allowedEmails={members.map((member) => member.email)}
                          value={selectedEmail ?? null}
                          onChange={(value) => {
                            onEmailChange(value ?? undefined);
                          }}
                          placeholder={t('githubActivity.memberSelectLabel')}
                          searchPlaceholder={t('githubActivity.memberSearchPlaceholder')}
                          emptyMessage={t('githubActivity.memberEmpty')}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
          <div className="ml-auto">
            <Select
              aria-label={t('githubActivity.periodSelectLabel')}
              options={periodOptions}
              value={days}
              onChange={(e) => {
                onDaysChange(e.target.value as PeriodDays);
              }}
              size="sm"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
